import { useTranslation } from 'react-i18next';
import { homeTasks, homeMainTasksKk, homeMainTasksRu, homeMainTasksEn } from '@/mocks/home';

export default function About() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'kk';

  const isEn = lang.startsWith('en');
  const isRu = lang.startsWith('ru');

  const mainTasks = isEn ? homeMainTasksEn : isRu ? homeMainTasksRu : homeMainTasksKk;

  return (
    <section id="about" className="relative bg-background-900 overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 tactical-grid opacity-10 pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 lg:py-32">
        {/* Section header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="section-label mb-4">
            {t('about.title', isEn ? 'About Federation' : isRu ? 'О Федерации' : 'Федерация туралы')}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-50 uppercase tracking-tight leading-tight">
            {isEn ? 'Federation Operations —' : isRu ? 'Деятельность Федерации —' : 'Федерация қызметі —'}
            <br />
            <span className="text-accent-500">
              {isEn
                ? 'developing military applied sports'
                : isRu
                ? 'развитие военно-прикладных видов спорта'
                : 'әскери-қолданбалы спорт түрлерін дамыту'}
            </span>
          </h2>
        </div>

        {/* Task cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-16 md:mb-24">
          {homeTasks.map((task) => {
            const title = isEn ? task.titleEn : isRu ? task.titleRu : task.titleKk;
            const description = isEn ? task.descriptionEn : isRu ? task.descriptionRu : task.descriptionKk;
            return (
              <div
                key={task.id}
                className="group relative bg-background-800/40 border border-background-700/40 p-6 md:p-8 hover:border-accent-500/30 transition-all duration-500"
              >
                {/* Icon */}
                <div className="w-12 h-12 flex items-center justify-center border border-accent-500/30 mb-5 group-hover:border-accent-500/60 transition-colors duration-300">
                  <i className={`${task.icon} text-accent-500 text-xl`} />
                </div>

                <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-100 uppercase tracking-wide mb-3">
                  {title}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {description}
                </p>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-accent-500 group-hover:w-full transition-all duration-500" />
              </div>
            );
          })}
        </div>

        {/* Main tasks */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
          <div>
            <div className="section-label mb-4">
              {isEn ? 'Key Objectives' : isRu ? 'Основные задачи' : 'Негізгі міндеттер'}
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 uppercase tracking-tight mb-6">
              {isEn ? 'Federation Objectives' : isRu ? 'Задачи Федерации' : 'Федерацияның міндеттері'}
            </h3>
            <p className="text-foreground-500 text-sm leading-relaxed mb-8">
              {t('about.desc', isEn
                ? 'The National Federation of Military Applied Sports unites top national athletes, advancing physical culture and combat readiness.'
                : isRu
                ? 'Национальная Федерация по военно-прикладным видам спорта Республики Казахстан объединяет лучших атлетов страны, развивает физическую культуру и боевую готовность.'
                : '«Әскери-қолданбалы спорт түрлерінің Ұлттық Федерациясы» қоғамдық бірлестігі елдің үздік атлеттерін біріктіреді, дене шынықтыру мен жауынгерлік дайындықты дамытады.')}
            </p>
          </div>

          <div className="space-y-4">
            {mainTasks.map((task, index) => (
              <div
                key={`main-task-${index}`}
                className="flex gap-4 items-start group"
              >
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-accent-500/20 text-accent-500 text-xs font-mono mt-0.5 group-hover:border-accent-500/50 transition-colors duration-300">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <p className="text-foreground-400 text-sm leading-relaxed pt-1.5">
                  {task}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-accent-500/20 to-transparent" />
    </section>
  );
}